import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import type { RepositoryControllerState, SubscriptionStore } from './RepositoryController';
import { RepositoryController } from './RepositoryController';

/**
 * Хук для подписки на состояние RepositoryController.
 * Возвращает актуальный снимок состояния: { data, isLoading, error }.
 *
 * Важно: контроллер находится вне React, поэтому подписка выполнена через
 * useSyncExternalStore для корректной работы с concurrent rendering.
 */
export function useRepositoryController<T extends Record<string, unknown>>(
  controller: RepositoryController<T>
): RepositoryControllerState<T> {
  return useSyncExternalStore(
    controller.subscribe.bind(controller),
    controller.getSnapshot.bind(controller),
    controller.getSnapshot.bind(controller)
  );
}

/**
 * Селектор состояния RepositoryController.
 * Позволяет изолировать подписку на конкретный срез состояния (например, только data).
 * Использует глубокое сравнение через JSON.stringify для объектов/массивов.
 *
 * Пример:
 *   const data = useRepositorySelector(ctrl, s => s.data);
 */
export function useRepositorySelector<T extends Record<string, unknown>, S>(
  controller: RepositoryController<T>,
  selector: (state: RepositoryControllerState<T>) => S
): S {
  return useStoreSelector(controller, selector);
}

/**
 * Селектор произвольного стора с подпиской (`SubscriptionStore`).
 *
 * Тот же механизм, что у `useRepositorySelector`, но не привязан к контроллеру:
 * рядом с ним живут сторы отдельных режимов (например `GroupsDataSource`), и
 * подписка на них не должна требовать копии этого хука.
 *
 * Пример:
 *   const groups = useStoreSelector(controller.getGroupsSource(), s => s.groups);
 */
export function useStoreSelector<State, S>(
  store: SubscriptionStore<State>,
  selector: (state: State) => S
): S {
  // Стабильные ссылки: иначе useSyncExternalStore переподписывался бы на каждый
  // рендер (bind создаёт новую функцию).
  const subscribe = useCallback((listener: () => void) => store.subscribe(listener), [store]);
  const getSnapshot = useCallback(() => store.getSnapshot(), [store]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const selectedRef = useRef<S | undefined>(undefined);

  return useMemo(() => {
    const selected = selector(state);
    // Глубокое сравнение для объектов/массивов
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        // Fallback на обычное сравнение если JSON.stringify не работает
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [state, selector]);
}
